import { z } from "zod";

import { resolvedPeriodSchema } from "./periods.ts";
import { quadrantSchema } from "./roles.ts";

/**
 * BCG-Matrix — geteilte Request-/Response-Shapes (FE + BE).
 *
 * Die Matrix ist eine **Portfolio-Sicht**: Ein Projektleiter, der in einem
 * Bereich mindestens einen Kunden betreut, sieht dort den ganzen Bereich
 * (`canViewSegmentBcg`) — der Vergleich mit dem Bereich ist gewollt. Damit die
 * Oberfläche trotzdem „nur meine Kunden" anbieten kann (Befund G1), liefert
 * jede Blase mit, ob der Angemeldete für sie Service Lead ist
 * (`isMyCustomer`), und die Antwort sagt einmal pro Segment, ob er ohnehin
 * jede Kundenakte des Segments öffnen darf (`viewer.canOpenAllCustomers`).
 *
 * Beides ist reine **Anzeige-Information** — der Zugriffsschutz bleibt an den
 * Endpunkten. Der Filter blendet aus, er sperrt nicht.
 */

export const bcgGranularitySchema = z.enum(["customer", "engagement"]);
export type BcgGranularity = z.infer<typeof bcgGranularitySchema>;

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Query für GET /bcg/matrix (Query-Strings sind immer Text → Coercion hier). */
export const bcgMatrixQuerySchema = z.object({
  segmentId: z.string().uuid(),
  periodKind: z.enum(["rolling_12m", "fiscal_year", "custom"]),
  periodStart: isoDateSchema.optional(),
  periodEnd: isoDateSchema.optional(),
  fiscalYearLabel: z.string().min(4).optional(),
  includeInactive: z
    .string()
    .optional()
    .transform((v) => v === "true"),
  /** Blase pro (Kunde × Segment)-Zelle oder pro Beauftragung (S2-11). */
  granularity: bcgGranularitySchema.default("customer"),
});
export type BcgMatrixQuery = z.input<typeof bcgMatrixQuerySchema>;

/** Query für GET /bcg/thresholds. */
export const bcgThresholdsQuerySchema = z.object({
  segmentId: z.string().uuid(),
  periodKind: z.enum(["rolling_12m", "fiscal_year", "custom"]),
  fiscalYearLabel: z.string().min(4).optional(),
  periodStart: isoDateSchema.optional(),
  periodEnd: isoDateSchema.optional(),
});

/** Body für PUT /bcg/thresholds. */
export const bcgUpdateThresholdsBodySchema = z.object({
  segmentId: z.string().uuid(),
  periodKind: z.enum(["rolling_12m", "fiscal_year", "custom"]),
  fiscalYearLabel: z.string().min(4).optional(),
  periodStart: isoDateSchema.optional(),
  periodEnd: isoDateSchema.optional(),
  revenueThreshold: z.number().nonnegative().optional(),
  growthThreshold: z.number().optional(),
});

export const bcgThresholdsSchema = z.object({
  revenue: z.number(),
  growth: z.number(),
  source: z.enum(["auto", "manual"]),
});
export type BcgThresholds = z.infer<typeof bcgThresholdsSchema>;

/** Eine Blase: (Kunde × Segment)-Zelle oder Beauftragung. */
export const bcgBubbleSchema = z.object({
  /** Kunden-ID bzw. Beauftragungs-ID — je nach `granularity`. */
  id: z.string().uuid(),
  /** Immer der Kundenname (Chart-Beschriftung). */
  name: z.string(),
  projectLeadId: z.string().uuid().nullable(),
  revenue: z.number(),
  /** `null` = Neukunde ohne Vorperiode; kein Wachstum berechenbar. */
  growth: z.number().nullable(),
  quadrant: quadrantSchema,
  marker: z.enum(["new", "inactive"]).nullable(),
  /**
   * Der Angemeldete ist Service Lead dieser Zelle (Quelle:
   * `customer_segment_leads`) bzw. dieser Beauftragung
   * (`engagements.lead_employee_id`). Trägt den Filter „nur meine Kunden".
   */
  isMyCustomer: z.boolean(),
  /** Nur granularity=engagement gesetzt. */
  customerId: z.string().uuid().optional(),
  engagementName: z.string().optional(),
  leadName: z.string().nullable().optional(),
  /** Wird in Phase 1.x ergänzt — derzeit nicht von der API geliefert. */
  lastOnsiteVisitDate: z.string().nullable().optional(),
});
export type BcgBubble = z.infer<typeof bcgBubbleSchema>;

export const bcgViewerSchema = z.object({
  /**
   * Der Angemeldete darf jede Kundenakte dieses Segments öffnen (Admin,
   * Geschäftsleitung, BL/Stellv. des Segments). Für alle anderen entscheidet
   * `isMyCustomer` je Blase — sie führt sonst in ein 403/404 (Befund G2).
   */
  canOpenAllCustomers: z.boolean(),
});

export const bcgMatrixResponseSchema = z.object({
  period: resolvedPeriodSchema,
  granularity: bcgGranularitySchema,
  thresholds: bcgThresholdsSchema,
  viewer: bcgViewerSchema,
  customers: z.array(bcgBubbleSchema),
});
export type BcgMatrixResponse = z.infer<typeof bcgMatrixResponseSchema>;

export const bcgThresholdsResponseSchema = z.object({
  segmentId: z.string().uuid(),
  periodKind: z.enum(["rolling_12m", "fiscal_year", "custom"]),
  fiscalYearLabel: z.string().optional(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  revenueThreshold: z.number(),
  growthThreshold: z.number(),
  source: z.enum(["auto", "manual"]),
});
export type BcgThresholdsResponse = z.infer<typeof bcgThresholdsResponseSchema>;
